import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011';

export interface RatingModalProps {
  roomId: string | null;
  accessToken: string | null;
  onClose: () => void;
  sessionDuration?: number; // in seconds
  promptUsed?: boolean;
}

export const RatingModal: React.FC<RatingModalProps> = ({
  roomId,
  accessToken,
  onClose,
  sessionDuration,
  promptUsed,
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Don't render if no roomId (no completed session)
  if (!roomId) return null;

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
    return `${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  const handleStarClick = (star: number) => {
    setRating(star);
  };

  const handleSubmit = async () => {
    if (rating === null) return;

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/sessions/room/${roomId}/rate`,
        { rating, feedback: feedback || undefined },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setIsSubmitted(true);

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit rating:', error);
      alert('Failed to submit rating. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  // Thank you message after submission
  if (isSubmitted) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.thankYouContainer}>
            <div style={styles.thankYouIcon}>🙏</div>
            <h2 style={styles.thankYouTitle}>Thank You!</h2>
            <p style={styles.thankYouMessage}>
              Your feedback helps us create meaningful connections.
            </p>
            <div style={styles.sparkles}>✨</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>How was your conversation?</h2>
          <p style={styles.subtitle}>
            {sessionDuration
              ? `You chatted for ${formatDuration(sessionDuration)}${promptUsed ? ' using a conversation starter' : ''}`
              : 'Rate your partner to help improve matches'}
          </p>
        </div>

        {/* Star Rating */}
        <div style={styles.ratingSection}>
          <div style={styles.stars}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleStarClick(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(null)}
                style={{
                  ...styles.star,
                  ...(star <= (hoveredRating || rating || 0) ? styles.starFilled : {}),
                }}
                disabled={isSubmitting}
              >
                {star <= (hoveredRating || rating || 0) ? '⭐' : '☆'}
              </button>
            ))}
          </div>

          {rating !== null && (
            <p style={styles.ratingLabel}>
              {rating === 5 && 'Amazing! 🎉'}
              {rating === 4 && 'Great! 😊'}
              {rating === 3 && 'Good 👍'}
              {rating === 2 && 'Okay 😐'}
              {rating === 1 && 'Not great 😔'}
            </p>
          )}
        </div>

        {/* Gratitude Moment - Optional Feedback */}
        {rating !== null && rating >= 3 && (
          <div style={styles.feedbackSection}>
            <label style={styles.feedbackLabel}>
              Care to share what made this meaningful? (optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Share your thoughts..."
              maxLength={1000}
              style={styles.feedbackTextarea}
              disabled={isSubmitting}
            />
            <div style={styles.charCount}>
              {feedback.length}/1000
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={styles.actions}>
          <button
            onClick={handleSkip}
            style={styles.skipButton}
            disabled={isSubmitting}
          >
            Skip
          </button>
          <button
            onClick={handleSubmit}
            style={{
              ...styles.submitButton,
              ...(rating === null || isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            disabled={rating === null || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '0.5rem',
    margin: 0,
  },
  subtitle: {
    fontSize: '1rem',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  ratingSection: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  stars: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  star: {
    fontSize: '3rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
    padding: '0.25rem',
    opacity: 0.5,
  },
  starFilled: {
    opacity: 1,
    transform: 'scale(1.1)',
  },
  ratingLabel: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--color-warm-orange)',
    margin: 0,
  },
  feedbackSection: {
    marginBottom: '1.5rem',
  },
  feedbackLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    marginBottom: '0.75rem',
    textAlign: 'center',
  },
  feedbackTextarea: {
    width: '100%',
    minHeight: '100px',
    padding: '0.75rem',
    border: '2px solid #e5e7eb',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  charCount: {
    textAlign: 'right',
    fontSize: '0.85rem',
    color: 'var(--color-gray-500)',
    marginTop: '0.25rem',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  skipButton: {
    padding: '0.75rem 2rem',
    backgroundColor: 'transparent',
    color: 'var(--color-gray-600)',
    border: '2px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  submitButton: {
    padding: '0.75rem 2rem',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  thankYouContainer: {
    textAlign: 'center',
    padding: '2rem',
  },
  thankYouIcon: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  thankYouTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
    margin: 0,
  },
  thankYouMessage: {
    fontSize: '1.1rem',
    color: 'var(--color-gray-600)',
    marginBottom: '1rem',
  },
  sparkles: {
    fontSize: '2rem',
  },
};
